import { createContext, useContext, useState, useCallback } from 'react'
import { CheckCircle, XCircle, X } from 'lucide-react'

// ══════════════════════════════════════════════════════════════════════
// TOAST CONTEXT
// ══════════════════════════════════════════════════════════════════════

const ToastContext = createContext(null)

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const toast = useCallback(({ message, type = 'success', duration = 3000 }) => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    }
  }, [])

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toast, dismissToast, toasts }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

// ══════════════════════════════════════════════════════════════════════
// TOAST COMPONENT
// ══════════════════════════════════════════════════════════════════════

export function Toast() {
  const { toasts, dismissToast } = useToast()

  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`
            flex items-center gap-3 px-5 py-3 rounded-xl shadow-elevated
            animate-slide-up min-w-[280px] max-w-md
            ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-havn-green-900 text-white'}
          `}
        >
          {toast.type === 'error' ? (
            <XCircle className="w-5 h-5 text-red-200 flex-shrink-0" />
          ) : (
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          )}
          <span className="text-sm font-medium flex-1">{toast.message}</span>
          <button
            onClick={() => dismissToast(toast.id)}
            className="p-1 hover:bg-white/10 rounded transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// CONVENIENCE HOOK
// ══════════════════════════════════════════════════════════════════════

export function useToastActions() {
  const { toast } = useToast()

  return {
    success: (message) => toast({ message, type: 'success' }),
    error: (message) => toast({ message, type: 'error' }),
  }
}
